/**
 * Column Registry — defines all available fields for Companies and Contacts tables.
 * Maps field keys to display configuration (label, type, default width, renderer info).
 */

// Canonical user-facing labels per the CRM tidy-up (2026-04-22).
// Keep these aligned with backend/models/account.py comments and
// backend/routes/data_import.py ACCOUNT_FIELDS / CONTACT_FIELDS.
export const COMPANY_FIELD_REGISTRY = {
    name: { label: 'Company', type: 'company_name', defaultWidth: 220, required: true },
    description: { label: 'Description', type: 'text', defaultWidth: 250 },
    industry: { label: 'Industry', type: 'text', defaultWidth: 140 },
    size: { label: 'Company Size', type: 'badge', defaultWidth: 130 },
    annual_revenue: { label: 'Revenue', type: 'currency', defaultWidth: 120 },
    status: { label: 'Status', type: 'status_badge', defaultWidth: 110 },
    health_score: { label: 'Health', type: 'progress', defaultWidth: 100 },
    domain: { label: 'Domain', type: 'url', defaultWidth: 150 },
    website: { label: 'Website URL', type: 'url', defaultWidth: 160 },
    phone: { label: 'Company Phone', type: 'text', defaultWidth: 140 },
    email: { label: 'Company Email', type: 'text', defaultWidth: 180 },
    address: { label: 'Street Address', type: 'text', defaultWidth: 180 },
    city: { label: 'City', type: 'text', defaultWidth: 120 },
    state: { label: 'State / Province', type: 'text', defaultWidth: 130 },
    country: { label: 'Country', type: 'text', defaultWidth: 120 },
    region: { label: 'Region', type: 'text', defaultWidth: 120 },
    hq_country: { label: 'Company HQ Country', type: 'text', defaultWidth: 160 },
    tags: { label: 'Categories', type: 'tags', defaultWidth: 180 },
    employee_range: { label: 'Employee Range', type: 'text', defaultWidth: 130 },
    num_employees: { label: 'Employee Number Approx', type: 'text', defaultWidth: 160 },
    founded_year: { label: 'Foundation Date', type: 'number', defaultWidth: 120 },
    funding_raised: { label: 'Funding Raised', type: 'text', defaultWidth: 130 },
    lead_investor: { label: 'Lead Investor', type: 'text', defaultWidth: 150 },
    investor_type: { label: 'Investor Type', type: 'badge', defaultWidth: 140 },
    // Social
    linkedin_url: { label: 'Company LinkedIn URL', type: 'social', defaultWidth: 180, platform: 'linkedin' },
    twitter_url: { label: 'Twitter', type: 'social', defaultWidth: 140, platform: 'twitter' },
    facebook_url: { label: 'Facebook', type: 'social', defaultWidth: 140, platform: 'facebook' },
    instagram_url: { label: 'Instagram', type: 'social', defaultWidth: 140, platform: 'instagram' },
    angellist_url: { label: 'AngelList', type: 'social', defaultWidth: 140, platform: 'angellist' },
    twitter_followers: { label: 'Twitter Followers', type: 'number', defaultWidth: 130 },
    // Computed
    connection_strength: { label: 'Connection Strength', type: 'connection_strength', defaultWidth: 160, computed: true },
    last_interaction_at: { label: 'Last Interaction', type: 'relative_time', defaultWidth: 150, computed: true },
    contact_count: { label: 'Contacts', type: 'number', defaultWidth: 90, computed: true },
    // Team
    team_members: { label: 'Team', type: 'team', defaultWidth: 160, sortable: false },
    owner_id: { label: 'Owner', type: 'owner', defaultWidth: 140 },
    created_at: { label: 'Created Date', type: 'date', defaultWidth: 120 },
    updated_at: { label: 'Updated', type: 'date', defaultWidth: 120 },
};

export const CONTACT_FIELD_REGISTRY = {
    full_name: { label: 'Name', type: 'contact_name', defaultWidth: 200, required: true },
    email: { label: 'Personal Email', type: 'text', defaultWidth: 200 },
    phone: { label: 'Personal Phone', type: 'text', defaultWidth: 140 },
    mobile: { label: 'Mobile/WhatsApp', type: 'text', defaultWidth: 140 },
    title: { label: 'Title', type: 'text', defaultWidth: 160 },
    department: { label: 'Department', type: 'text', defaultWidth: 130 },
    seniority: { label: 'Seniority', type: 'badge', defaultWidth: 110 },
    key_role: { label: 'Key Role', type: 'key_role', defaultWidth: 120 },
    account_name: { label: 'Company', type: 'text', defaultWidth: 150, sortable: false },
    // #864: "Contact Owner" (companies keep "Owner"); an unowned contact reads
    // "No Contact Owner" (companies keep "Unassigned").
    owner_id: { label: 'Contact Owner', type: 'owner', defaultWidth: 140, noOwnerLabel: 'No Contact Owner' },
    disc_profile: { label: 'DISC', type: 'disc', defaultWidth: 80 },
    role_in_deal: { label: 'Role', type: 'badge', defaultWidth: 130 },
    engagement_score: { label: 'Engagement', type: 'progress', defaultWidth: 110 },
    influence_score: { label: 'Influence', type: 'progress', defaultWidth: 110 },
    relationship_strength: { label: 'Relationship', type: 'badge', defaultWidth: 120 },
    linkedin_url: { label: 'Personal LinkedIn URL', type: 'social', defaultWidth: 180, platform: 'linkedin' },
    tags: { label: 'Tags', type: 'tags', defaultWidth: 160 },
    created_at: { label: 'Created', type: 'date', defaultWidth: 120 },
};

// Default visible columns for Companies
// #534: Owner is a default on both lists — index 4 in each set, and the same
// key (`owner_id`) so both lists render Owner identically through OwnerCell.
export const DEFAULT_COMPANY_COLUMNS = [
    'name', 'industry', 'annual_revenue', 'status', 'owner_id', 'team_members',
    'tags', 'connection_strength', 'last_interaction_at', 'created_at',
];

// Default visible columns for Contacts
export const DEFAULT_CONTACT_COLUMNS = [
    'full_name', 'email', 'title', 'account_name', 'owner_id',
    'disc_profile', 'role_in_deal', 'engagement_score', 'tags',
];

/**
 * Build TanStack column definitions from registry + view config + custom fields.
 */
export function buildVisibleColumns(registry, viewColumns, customFields = []) {
    const allFields = { ...registry };

    // Add custom fields
    customFields.forEach(cf => {
        allFields[`cf_${cf.key}`] = {
            label: cf.label,
            type: cf.field_type || 'text',
            defaultWidth: 140,
            isCustom: true,
        };
    });

    // If view columns exist, use them for ordering/visibility
    if (viewColumns && viewColumns.length > 0) {
        return viewColumns
            .filter(vc => vc.visible !== false && allFields[vc.field_key])
            .map(vc => ({
                key: vc.field_key,
                ...allFields[vc.field_key],
                width: vc.width || allFields[vc.field_key].defaultWidth,
                label: vc.label || allFields[vc.field_key].label,
            }));
    }

    // Default: return required + registry order
    return Object.entries(allFields)
        .filter(([key]) => DEFAULT_COMPANY_COLUMNS.includes(key))
        .map(([key, config]) => ({
            key,
            ...config,
            width: config.defaultWidth,
        }));
}

// #1000: the lists sort on the server, so a column sorts only when the API can sort it.
// `computed: true` columns are worked out per page after the query and `sortable: false`
// ones are derived (team_members) or looked up (account_name); custom fields (`cf_*`)
// sort while their definition is active. backend/routes/{accounts,contacts}.py keep the
// matching allow-lists and their drift tests read these flags.
export function isSortableColumn(key, registry) {
    if (!key) return false;
    if (key.startsWith('cf_')) return true;
    const field = registry[key];
    return Boolean(field) && !field.computed && field.sortable !== false;
}

export function getAllFields(registry, customFields = []) {
    const result = Object.entries(registry).map(([key, config]) => ({
        key,
        ...config,
    }));

    customFields.forEach(cf => {
        result.push({
            key: `cf_${cf.key}`,
            label: cf.label,
            type: cf.field_type || 'text',
            defaultWidth: 140,
            isCustom: true,
        });
    });

    return result;
}
